import {ServiceSuccessResponseType} from './service-mappers/BaseMapper';
import {AxiosCall} from '../axios';
import {environment} from '../environment/environment';
import {
  GuarantorClinicStatementsPaymentServiceMapper,
  IPatientTicketAndApptSummary,
  PatientInvoiceLineMap,
  VerifyGuarantorTokenPaymentServiceMapper
} from './service-mappers/PaymentServiceMapper';
import {serializeQueryParams} from './PaymentHistoryService';

const baseGuarantorPaymentUrl = environment.apiEndPoints.guarantorPayments.base;
const basePaymentUrl = environment.apiEndPoints.guarantorPayments.payment;

export async function verifyGuarantorCodeToken(
  code: string,
  portal_type: string,
  params?: Record<string, any>
): Promise<ServiceSuccessResponseType<{token: string}>> {
  let queryParams = '';
  if (params?.is_appointment_portal) {
    queryParams += `?is_appointment_portal=${params?.is_appointment_portal}`;
  }
  const url =
    basePaymentUrl +
    environment.apiEndPoints.guarantorPayments.endPoints.getToken;
  return AxiosCall.get(`${url}/${code}/${portal_type}${queryParams}`);
}

export async function verifyGuarantorToken(
  clinicId?: number
): Promise<
  ServiceSuccessResponseType<VerifyGuarantorTokenPaymentServiceMapper>
> {
  let params = '';
  if (clinicId) {
    params += `?selected_clinic_id=${clinicId}`;
  }
  const url =
    baseGuarantorPaymentUrl +
    environment.apiEndPoints.guarantorPayments.endPoints.verifyToken +
    params;
  return AxiosCall.get(`${url}`);
}

export async function verifyGuarantorLoginDetails(
  params: {
    guarantor_name: string;
    patient_dob: string;
    patient_first_name: string;
    attempt: number;
  },
  clinicId?: number
): Promise<ServiceSuccessResponseType<{token: string}>> {
  let queryParams = '';
  if (clinicId) {
    queryParams += `?selected_clinic_id=${clinicId}`;
  }
  const url =
    baseGuarantorPaymentUrl +
    environment.apiEndPoints.guarantorPayments.endPoints.login +
    queryParams;
  return AxiosCall.post(`${url}`, {...params});
}

export async function fetchGuarantorPatients(): Promise<
  ServiceSuccessResponseType<{
    current_due_amount: number;
    amount_paid: number;
    balance_forward: number;
    clinic_statements: Array<GuarantorClinicStatementsPaymentServiceMapper>;
  }>
> {
  const url =
    baseGuarantorPaymentUrl +
    environment.apiEndPoints.guarantorPayments.endPoints.patientDetails;
  return AxiosCall.get(`${url}`);
}

export async function fetchPatientsInvoiceLines(
  invoice_id: number,
  clinic_id: number
): Promise<ServiceSuccessResponseType<Array<PatientInvoiceLineMap>>> {
  let url =
    baseGuarantorPaymentUrl +
    environment.apiEndPoints.guarantorPayments.endPoints.patientInvoiceLines;
  url = url.replace(/:patient_id/g, invoice_id.toString());
  return AxiosCall.get(`${url}?clinic_id=${clinic_id}`);
}

export const sendReminderEmail = async (payload: {
  email_id: string;
}): Promise<ServiceSuccessResponseType<null>> => {
  const url =
    baseGuarantorPaymentUrl +
    environment.apiEndPoints.guarantorPayments.endPoints.sendEmail;
  return AxiosCall.post(url, payload);
};

export const fetchGuarantorPaymentHistory = async (): Promise<
  ServiceSuccessResponseType<any>
> => {
  const url =
    baseGuarantorPaymentUrl +
    environment.apiEndPoints.guarantorPayments.endPoints.paymentHistory;
  return AxiosCall.get(url);
};

export const refundTransaction = async (params: {
  clinicGroupId: string;
  payload: {
    clinic_id: string;
    invoice_number: string;
    amount: number;
    reason: string;
    settle_statement: boolean;
    payment_method: string;
  };
}): Promise<any> => {
  const {clinicGroupId, payload} = params;
  return AxiosCall.post(`/clinic-group/${clinicGroupId}/refund`, payload);
};

export const guarantorRefresh = async (
  id: number
): Promise<ServiceSuccessResponseType<{token: string}>> => {
  return AxiosCall.post('/guarantor/refresh', {location_id: id});
};

export const createPlanForGuarantor = async (
  clinic_group_id: number | string,
  clinic_id: string | number,
  payload: any
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/payment-plan/create`;
  return AxiosCall.post(url, payload);
};

export const createPlanFromGuarantorPortal = async (
  guarantor_id: string | number,
  payload: any
): Promise<any> => {
  const url = `/guarantor/${guarantor_id}/payment-plan/create`;
  return AxiosCall.post(url, payload);
};

export const generatePaymentAmounts = async (
  clinic_group_id: string | number,
  clinic_id: any,
  payload: any
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/payment-amounts`;
  return AxiosCall.post(url, payload);
};

export const generatePaymentPlanAmountGuarantorPortal = async (
  payload: any
): Promise<any> => {
  const url = `/guarantor/payment-amounts`;
  return AxiosCall.post(url, payload);
};

export const retryPaymentPlanInstalmentLine = async (
  clinic_group_id: number,
  clinic_id: number,
  payment_plan_instalment_line_id: number
): Promise<any> => {
  const url = `/clinic-group/${clinic_group_id}/clinic/${clinic_id}/retry/payment-plan-instalment-line`;
  const payload = {
    payment_plan_instalment_line_id
  };
  return AxiosCall.post(url, payload);
};

export async function fetchPatientTicketAndAppointmentSummary(): Promise<
  ServiceSuccessResponseType<{patients: IPatientTicketAndApptSummary[]}>
> {
  const url =
    baseGuarantorPaymentUrl +
    environment.apiEndPoints.guarantorPayments.endPoints.patientDetails +
    '/ticket-and-appointment-summary';
  return AxiosCall.get(`${url}`);
}

export async function fetchPatientForm({
  params
}: {
  params: {
    clinic_id: number;
  };
}): Promise<any> {
  let queryParams = '';
  if (params) {
    queryParams = serializeQueryParams(params);
  }
  return AxiosCall.getFile({
    url: `/guarantor/patient-forms/download-file?${queryParams}`
  });
}

export async function uploadPatientForm({
  payload
}: {
  payload: any;
}): Promise<any> {
  return AxiosCall.post(`/guarantor/patient-forms/upload`, payload);
}

export async function fetchGuarantorInfoForm({
  token
}: {
  token: string;
}): Promise<any> {
  return AxiosCall.get(
    `/voice-assistant/appointment/guarantor-info/${encodeURIComponent(token)}`
  );
}

export async function fetchGuarantorInfoPrefill({
  token,
  patient_id
}: {
  token: string;
  patient_id: number;
}): Promise<any> {
  return AxiosCall.get(
    `/voice-assistant/appointment/guarantor-info/${encodeURIComponent(token)}/prefill?patient_id=${patient_id}`
  );
}

export async function saveGuarantorInfoForm({
  token,
  payload
}: {
  token: string;
  payload: any;
}): Promise<any> {
  return AxiosCall.post(
    `/voice-assistant/appointment/guarantor-info/${encodeURIComponent(token)}`,
    payload
  );
}
